import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListLandingPagesResult } from '@whinerp/client';
import { Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/** The hosted landing pages, and which are live (F-072). The way into SC-63. */
export function LandingPagesPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const enabled = useEntitlement('F-072');
  const { can } = useSession();
  const pages = useQuery<ListLandingPagesResult, ApiError>({
    queryKey: ['landing-pages'],
    queryFn: () => api.listLandingPages({}),
    enabled: enabled.enabled,
  });
  if (!enabled.enabled) return null;
  return (
    <Panel data-region="landing-pages">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('landing.pages')}</SectionLabel>
          {can('campaign.send') ? (
            <Link data-landing-new to="/landing-pages/new" className="ms-auto text-dense text-bronze-text hover:text-ink">
              {t('landing.new')}
            </Link>
          ) : null}
        </div>
        {pages.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : pages.isError ? (
          <p className="text-dense text-danger-text">{errorText(pages.error)}</p>
        ) : pages.data.pages.length === 0 ? (
          <p data-landing-none className="text-meta text-ink-meta">{t('landing.none')}</p>
        ) : (
          <ul className="flex flex-col">
            {pages.data.pages.map((page) => (
              <li key={page.pageId} data-landing-page={page.pageId} className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0">
                <Link to={`/landing-pages/${page.pageId}`} className="min-w-0 flex-1 truncate text-body text-ink hover:underline">
                  {page.name}
                </Link>
                <span className="text-meta text-ink-meta">
                  {page.publicPath
                    ? t(page.publishedVersion === page.draftVersion ? 'landing.liveCurrent' : 'landing.liveOlder', { values: { path: page.publicPath } })
                    : t('landing.draftOnly')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
